import { handleResponse, handleError, handleFileResponse, handleFileBlobResponse, getJsonRequestInit, getFormDataRequestInit } from "./baseService";
import { Session } from "../session/session";
import { IEndlessList, EndlessList } from "ui/Scripts/source/models/common/endlessList";
import { Base } from "ui/Scripts/source/framework/base";

class AbortItem {
    id: string;
    controller: AbortController;

    constructor() {
        this.id = Base.getGuid();
        this.controller = new AbortController();
    }
}

class AbortManager {
    items: AbortItem[];

    constructor() {
        this.items = [];
    }
    
    get(): AbortItem {
        const result = new AbortItem();
        this.items.push(result);
        return result;
    }

    remove(id: string) {
        this.items = this.items.filter(i => i.id !== id);
    }

    abort(id: string) {
        if (!id) return;
        const item = this.items.find(i => i.id === id);
        if (!item) return;
        item.controller.abort();
    }

    abortAll() {
        for (const item of this.items) {
            item.controller.abort();
        }
    }

    clean() {
        this.items = [];
    }
}

export const abortManager = new AbortManager();

export class AbortDictionary {
    abortIds: { [id: string]: string };

    constructor() {
        this.abortIds = {};
    }
    
    add(id: string, abortId: string) {
        if (!id) return;
        this.abortIds[id] = abortId;
    }
    
    remove(id: string) {
        if (!id) return;
        if (Object.prototype.hasOwnProperty.call(this.abortIds, id)) {
            delete this.abortIds[id];
        }
    }

    abort(id: string) {
        if (!id) return;
        if (Object.prototype.hasOwnProperty.call(this.abortIds, id)) {
            abortManager.abort(this.abortIds[id]);
            delete this.abortIds[id];
        }
    }

    abortAll() {
        try {
            for (const id in this.abortIds) {
                this.abort(id);
            }
        } catch (e) {
            console.log("abortAll", e);
        }
    }
}

export const logout = async () : Promise<void> => {
    await Session.setSessionState(null);
    window.document.write('<meta name="referrer" content="no-referrer"/>');
    window.location.href = appConfig.logoutUrl;
    return new Promise<void>((resolve) => { resolve(); });
};

export const navigateTo = (url: string) => {
    abortManager.abortAll();
    abortManager.clean();
    window.location.href = appConfig.rootUrl + url;
};

export const getApiCall = async <T>(url: string, type: (new (...args: any[]) => T), setAbortItem?: (id: string) => void): Promise<T> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        const json = await handleResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal)));
        /* eslint-disable new-cap */
        return type
            ? !json ? new type() : new type(json)
            : !json ? <T><unknown>0 : <T>(json.result);
        /* eslint-enable new-cap */
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const getFileApiCall = async (url: string, setAbortItem?: (id: string) => void): Promise<string> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        return await handleFileResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal)));
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const postFileApiCall = async (url: string, body?: string, setAbortItem?: (id: string) => void): Promise<string> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        return await handleFileResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal, "POST", body)));
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const postFormDataFileApiCall = async (url: string, data: FormData, setAbortItem?: (id: string) => void): Promise<string> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        return await handleFileResponse(await fetch(appConfig.rootUrl + url, getFormDataRequestInit(item.controller.signal, "POST", data)));
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const getFileBlobApiCall = async (url: string, setAbortItem?: (id: string) => void): Promise<Blob> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        return await handleFileBlobResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal)));
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const postFileBlobApiCall = async (url: string, body?: string, setAbortItem?: (id: string) => void): Promise<Blob> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        return await handleFileBlobResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal, "POST", body)));
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const postApiCall = async <T>(url: string, type: (new (...args: any[]) => T), body?: string, setAbortItem?: (id: string) => void, skipLoginRedirect: boolean = false): Promise<T> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        const json = await handleResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal, "POST", body)), true, skipLoginRedirect);
        /* eslint-disable new-cap */
        return type
            ? !json ? new type() : new type(json)
            : !json ? <T><unknown>0 : <T>(json.result);
        /* eslint-enable new-cap */
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

//export const creatorPostApiCall = async <T>(url: string, creator: (...args: any[]) => T, body?: string): Promise<T> => {
//    const item = abortManager.get();
//    try {
//        const json = await handleResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal, "POST", body)));
//        /* eslint-disable new-cap */
//        return creator
//            ? !json ? creator() : creator(json)
//            : !json ? <T><unknown>0 : <T>(json.result);
//        /* eslint-enable new-cap */
//    } catch (e) {
//        return handleError(e);
//    } finally {
//        abortManager.remove(item.id);
//    }
//};

export const postFormApiCall = async <T>(url: string, data: FormData, type: (new (...args: any[]) => T), setAbortItem?: (id: string) => void): Promise<T> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        const json = await handleResponse(await fetch(appConfig.rootUrl + url, getFormDataRequestInit(item.controller.signal, "POST", data)));
        /* eslint-disable new-cap */
        return type
            ? !json ? new type() : new type(json)
            : !json ? <T><unknown>0 : <T>(json.result);
        /* eslint-enable new-cap */
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};

export const postEndlessListApiCall = async <T>(url: string, type: (new (...args: any[]) => T), body?: string, setAbortItem?: (id: string) => void): Promise<IEndlessList<T>> => {
    const item = abortManager.get();
    if (setAbortItem) {
        setAbortItem(item.id);
    }
    try {
        const json = await handleResponse(await fetch(appConfig.rootUrl + url, getJsonRequestInit(item.controller.signal, "POST", body)));
        /* eslint-disable new-cap */
        return !json ? new EndlessList(null, type) : new EndlessList(json, type);
        /* eslint-enable new-cap */
    } catch (e) {
        return handleError(e);
    } finally {
        abortManager.remove(item.id);
    }
};